import { useState, useEffect } from "react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/formatters";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Activity } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABELS = {
  create: "Creazione", update: "Modifica", issue: "Emissione",
  version: "Nuova versione", cancel: "Annullamento", archive: "Archiviazione",
  delete: "Eliminazione", upload: "Caricamento file", deactivate: "Disattivazione",
  settings_change: "Modifica impostazioni"
};

const ENTITY_LABELS = {
  quote: "Preventivo", inventory: "Inventario", proforma: "Fattura Proforma",
  customer: "Cliente", user: "Utente", organization: "Organizzazione",
  header_template: "Template", attachment: "Allegato"
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [entityFilter, setEntityFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      const params = new URLSearchParams();
      if (entityFilter && entityFilter !== "all") params.set("entity_type", entityFilter);
      if (actionFilter && actionFilter !== "all") params.set("action", actionFilter);
      params.set("limit", "100");
      const { data } = await api.get(`/audit-logs?${params}`);
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Errore nel caricamento log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, [entityFilter, actionFilter]);

  return (
    <div data-testid="audit-log-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tighter font-['Outfit']">Log Attività</h1>
        <p className="text-sm text-zinc-500">{total} eventi registrati</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger data-testid="audit-entity-filter" className="w-[180px] rounded-sm"><SelectValue placeholder="Tipo entità" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le entità</SelectItem>
            {Object.entries(ENTITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger data-testid="audit-action-filter" className="w-[180px] rounded-sm"><SelectValue placeholder="Azione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le azioni</SelectItem>
            {Object.entries(ACTION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Data/Ora</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Utente</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Azione</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Entità</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden md:table-cell">Dettagli</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-b border-zinc-100" data-testid={`audit-row-${log.id}`}>
                  <td className="px-5 py-3 text-xs text-zinc-500 font-mono whitespace-nowrap">{formatDateTime(log.timestamp)}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600">{log.user_email}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1 text-xs font-medium">
                      <Activity className="w-3 h-3" />
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm">{ENTITY_LABELS[log.entity_type] || log.entity_type}</td>
                  <td className="px-5 py-3 text-xs text-zinc-500 hidden md:table-cell max-w-[200px] truncate">
                    {log.details ? JSON.stringify(log.details).slice(0, 100) : "-"}
                  </td>
                </tr>
              ))}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-zinc-400">Nessun evento trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
